import { useCallback, useEffect, useRef, useState } from 'react';
import { T, useGT } from 'gt-react';
import { EnvelopeIcon, SpinnerIcon, WarningIcon } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { orpc } from '@/api/rpc/client';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from 'src/context/ToastContext';
import { useArCustomerContacts } from '@/api/rpc/hooks/accounts-receivable/useArCustomerContacts';
import { useArInvoiceEmail } from '@/api/rpc/hooks/accounts-receivable/useArInvoiceEmail';

interface SendInvoiceDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  invoiceId: string;
  customerId: string;
  customerName: string | null;
  invoiceNumber: string | null;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PLACEHOLDER_EMAIL = 'preview@placeholder.local';

export function SendInvoiceDialog({
  open,
  onOpenChange,
  invoiceId,
  customerId,
  customerName,
  invoiceNumber,
}: SendInvoiceDialogProps) {
  const gt = useGT();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { contacts, isLoading: contactsLoading } =
    useArCustomerContacts(customerId);
  const { send, isSending, preview, isPreviewing, previewData } =
    useArInvoiceEmail();

  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [customEmail, setCustomEmail] = useState('');
  const [includeCustom, setIncludeCustom] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [previewTab, setPreviewTab] = useState('email');

  const fetchPreview = useCallback(() => {
    setPreviewError(false);
    preview({
      invoiceId,
      recipientEmails: [PLACEHOLDER_EMAIL],
    }).catch(() => {
      setPreviewError(true);
    });
  }, [invoiceId, preview]);

  // Fetch preview once when dialog opens
  const hasFetchedPreview = useRef(false);
  useEffect(() => {
    if (!open) {
      hasFetchedPreview.current = false;
      return;
    }
    if (!hasFetchedPreview.current) {
      hasFetchedPreview.current = true;
      fetchPreview();
    }
  }, [open, fetchPreview]);

  const emailContacts = contacts.filter((c) => c.email);

  function isInvoiceContact(
    originMetadata: Record<string, unknown> | null,
  ): boolean {
    if (!originMetadata) return false;
    const taskName = originMetadata.workflowTaskName;
    return (
      typeof taskName === 'string' && taskName.toLowerCase().includes('invoice')
    );
  }

  function toggleEmail(email: string) {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) {
        next.delete(email);
      } else {
        next.add(email);
      }
      return next;
    });
  }

  const customEmailValid =
    includeCustom && EMAIL_REGEX.test(customEmail.trim());
  const allRecipients = [
    ...selectedEmails,
    ...(customEmailValid ? [customEmail.trim()] : []),
  ];
  const hasRecipients = allRecipients.length > 0;
  const [toEmail, ...ccEmails] = allRecipients;

  async function handleConfirmedSend() {
    if (!hasRecipients) return;
    try {
      await send({
        invoiceId,
        recipientEmails: allRecipients,
      });
      setShowConfirm(false);
      handleOpenChange(false);
      // Invalidate invoice detail query so isSent and invoiceEvents refresh
      queryClient.invalidateQueries({
        queryKey: orpc.accountsReceivable.arInvoices.get.key({
          input: { id: invoiceId },
        }),
      });
      showToast({
        title: 'Invoice sent',
        message: `Invoice emailed to ${allRecipients.join(', ')}`,
        color: 'success',
        icon: 'check',
      });
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to send invoice';
      showToast({
        title: 'Send failed',
        message,
        color: 'error',
        icon: 'warning',
      });
    }
  }

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen) {
      setSelectedEmails(new Set());
      setCustomEmail('');
      setIncludeCustom(false);
      setPreviewError(false);
      setShowConfirm(false);
      setPreviewTab('email');
    }
    onOpenChange(nextOpen);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-3xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>
            <T context="dialog title for sending an invoice">Send Invoice</T>
            {invoiceNumber ? ` — ${invoiceNumber}` : ''}
            {customerName ? ` (${customerName})` : ''}
          </DialogTitle>
        </DialogHeader>

        <div className="min-h-0 flex-1 flex flex-col gap-4 overflow-auto">
          {/* ── Recipients ─────────────────────────────────────── */}
          <div className="space-y-2">
            <p className="text-sm font-medium">
              <T context="label for email recipients section">Recipients</T>
            </p>
            {contactsLoading ? (
              <div className="flex items-center gap-2 py-2 text-sm text-muted-foreground">
                <SpinnerIcon className="h-4 w-4 animate-spin" />
                <T context="loading state for contacts">Loading contacts...</T>
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {emailContacts.map((contact) => {
                  const email = contact.email ?? '';
                  const isInvoice = isInvoiceContact(contact.originMetadata);
                  return (
                    <div
                      key={contact.id}
                      className="flex items-center space-x-2 rounded-md border p-2"
                    >
                      <Checkbox
                        id={`contact-${contact.id}`}
                        checked={selectedEmails.has(email)}
                        onCheckedChange={() => toggleEmail(email)}
                      />
                      <Label
                        htmlFor={`contact-${contact.id}`}
                        className="flex cursor-pointer items-center gap-2"
                      >
                        <div>
                          {contact.name && (
                            <div className="text-sm font-medium">
                              {contact.name}
                            </div>
                          )}
                          <div className="text-xs text-muted-foreground">
                            {email}
                          </div>
                        </div>
                        {isInvoice && (
                          <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-primary">
                            <T context="badge label for invoice contact type">
                              Invoice
                            </T>
                          </span>
                        )}
                      </Label>
                    </div>
                  );
                })}

                <div className="flex items-center space-x-2 rounded-md border p-2">
                  <Checkbox
                    id="contact-custom"
                    checked={includeCustom}
                    onCheckedChange={(checked) =>
                      setIncludeCustom(checked === true)
                    }
                  />
                  <Label
                    htmlFor="contact-custom"
                    className="cursor-pointer text-sm font-medium"
                  >
                    <T context="label for custom recipient option">Other</T>
                  </Label>
                </div>
              </div>
            )}

            {includeCustom && (
              <Input
                type="email"
                placeholder={gt('recipient@example.com', {
                  context: 'custom recipient email input placeholder',
                })}
                value={customEmail}
                onChange={(e) => setCustomEmail(e.target.value)}
                className="max-w-xs"
                autoFocus
              />
            )}

            {hasRecipients && (
              <div className="space-y-0.5 text-xs text-muted-foreground">
                {previewData && (
                  <div>
                    <span className="font-medium">
                      <T context="email sender label">From:</T>
                    </span>{' '}
                    {previewData.from}
                  </div>
                )}
                <div>
                  <span className="font-medium">
                    <T context="email recipient label">To:</T>
                  </span>{' '}
                  {toEmail}
                </div>
                {ccEmails.length > 0 && (
                  <div>
                    <span className="font-medium">
                      <T context="email CC label">CC:</T>
                    </span>{' '}
                    {ccEmails.join(', ')}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Preview ────────────────────────────────────────── */}
          {!previewData && isPreviewing ? (
            <div
              className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground"
              style={{ minHeight: 500 }}
            >
              <SpinnerIcon className="h-4 w-4 animate-spin" />
              <T context="loading state for email preview">
                Loading preview...
              </T>
            </div>
          ) : !previewData && previewError ? (
            <div
              className="flex flex-1 items-center justify-center gap-2 text-sm text-muted-foreground"
              style={{ minHeight: 500 }}
            >
              <WarningIcon className="h-4 w-4" />
              <T>Failed to load preview</T>
            </div>
          ) : previewData ? (
            <>
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <span className="font-medium">
                  <T context="email subject label">Subject:</T>
                </span>{' '}
                {previewData.subject}
                {isPreviewing && (
                  <SpinnerIcon className="h-3 w-3 animate-spin" />
                )}
              </div>
              <Tabs
                value={previewTab}
                onValueChange={setPreviewTab}
                className="min-h-0 flex-1 flex flex-col"
              >
                <TabsList className="w-fit">
                  <TabsTrigger value="email">
                    <T context="tab label for email body preview">Email Body</T>
                  </TabsTrigger>
                  <TabsTrigger value="pdf">
                    <T context="tab label for PDF attachment preview">
                      PDF Attachment
                    </T>
                  </TabsTrigger>
                </TabsList>
                <TabsContent
                  value="email"
                  className="min-h-0 flex-1 overflow-auto rounded border bg-white"
                >
                  <iframe
                    title={gt('Email preview', {
                      context: 'iframe title for email body preview',
                    })}
                    srcDoc={previewData.htmlContent}
                    className="h-full w-full border-0"
                    style={{ minHeight: 500 }}
                    sandbox=""
                  />
                </TabsContent>
                <TabsContent
                  value="pdf"
                  className="min-h-0 flex-1 overflow-auto"
                >
                  <iframe
                    title={gt('PDF attachment preview', {
                      context: 'iframe title for PDF attachment preview',
                    })}
                    src={`data:application/pdf;base64,${previewData.pdfBase64}`}
                    className="h-full w-full rounded border"
                    style={{ minHeight: 600 }}
                  />
                </TabsContent>
              </Tabs>
            </>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            <T context="button label to cancel sending invoice">Cancel</T>
          </Button>
          <Button
            onClick={() => setShowConfirm(true)}
            disabled={!hasRecipients}
          >
            <EnvelopeIcon className="mr-1.5 h-4 w-4" />
            <T context="button label to send invoice email">Send Invoice</T>
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* ── Confirm dialog ──────────────────────────────────── */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>
              <T context="confirmation dialog title for sending invoice">
                Confirm Send
              </T>
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            <T context="confirmation message for sending invoice to recipient">
              Send this invoice to
            </T>{' '}
            <span className="font-medium text-foreground">{toEmail}</span>
            {ccEmails.length > 0 && (
              <>
                {' '}
                <T context="CC recipient part of confirmation message">
                  with CC to
                </T>{' '}
                <span className="font-medium text-foreground">
                  {ccEmails.join(', ')}
                </span>
              </>
            )}
            ?
          </p>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowConfirm(false)}
              disabled={isSending}
            >
              <T context="button label to cancel sending confirmation">
                Cancel
              </T>
            </Button>
            <Button onClick={handleConfirmedSend} disabled={isSending}>
              {isSending ? (
                <>
                  <SpinnerIcon className="mr-1.5 h-4 w-4 animate-spin" />
                  <T context="button label while sending invoice">Sending...</T>
                </>
              ) : (
                <T context="button label to confirm and send invoice">
                  Confirm &amp; Send
                </T>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
